/**
 * T-d3 (no silent cap) for the contact list (W4, B2-liste).
 *
 * The dialing engine loads the queue once, capped: `DialerDataContext` Q1
 * reads the ENGINE_QUEUE_LIMIT most recent ProspectQueue records (scoped to
 * the selected customer) and Power Mode picks its batches from them. That
 * path is the live call cycle and stays as it is.
 *
 * The list on screen must not stop at that cap without saying so. When the
 * engine's queue comes back full (exactly at the cap, so more may exist), the
 * screen reads the whole list through the W2 paginated route
 * `POST /api/entities/ProspectQueue/page` (cursor, 200 per page, same
 * authorization chain as /filter) and displays every contact. Dial
 * eligibility stays on the record's own projection (ENG-5, `call_history`),
 * which every record carries, loaded calls or not.
 *
 * Below the cap nothing extra is fetched: the engine's queue is the list.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

/** Mirrors the limit of `DialerDataContext` Q1 (the engine's queue read). */
export const ENGINE_QUEUE_LIMIT = 1000;
export const LIST_PAGE_SIZE = 200;
/** 100 pages × 200 = 20 000 contacts read for display; beyond, the screen says « N affichés sur T ». */
export const MAX_LIST_PAGES = 100;
/**
 * Older contacts change rarely: the full read is refreshed every minute, and
 * after the list's own writes (`invalidateProspectListAllPages`). Not after
 * every queue invalidation: SSE `queue:changed`, dial start, qualification
 * and stop invalidate `['prospect-queue']` dozens of times a session, and a
 * read of up to MAX_LIST_PAGES pages must not restart on each of them
 * (REV-W4-F1).
 */
export const BEYOND_CAP_REFRESH_MS = 60_000;
/** Own prefix, outside `['prospect-queue']` on purpose (see above). */
export const PROSPECT_LIST_ALL_PAGES_KEY = 'prospect-list-all-pages';

/** After a write made from the list (status, priority, delete, rename...). */
export function invalidateProspectListAllPages(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [PROSPECT_LIST_ALL_PAGES_KEY] });
}

interface PageResponse<Row> { items: Row[]; next_cursor: string | null; total?: number }

type Post = <T>(path: string, body: unknown, options?: { signal?: AbortSignal }) => Promise<T>;

export interface AllPages<Row> { items: Row[]; total: number | null; complete: boolean }

export async function fetchAllProspectPages<Row>(
  filter: Record<string, unknown>,
  post: Post = (path, body, options) => apiClient.post(path, body, options),
  maxPages = MAX_LIST_PAGES,
  signal?: AbortSignal,
): Promise<AllPages<Row>> {
  const items: Row[] = [];
  let total: number | null = null;
  let cursor: string | null = null;
  for (let page = 0; page < maxPages; page += 1) {
    // A cancelled read (new refetch, unmount) stops between pages instead of
    // running its remaining pages in the background (REV-W4-F1).
    signal?.throwIfAborted();
    const res: PageResponse<Row> = await post<PageResponse<Row>>('/entities/ProspectQueue/page', {
      filter,
      sort: '-created_date',
      limit: LIST_PAGE_SIZE,
      ...(page === 0 ? { include_total: true } : {}),
      ...(cursor ? { cursor } : {}),
    }, signal ? { signal } : undefined);
    if (page === 0 && typeof res.total === 'number') total = res.total;
    items.push(...(res.items ?? []));
    cursor = res.next_cursor;
    if (!cursor) return { items, total, complete: true };
  }
  return { items, total, complete: false };
}

/**
 * The engine's rows first (fresher: they poll every 5 s on this screen), in
 * their order, then every paged row the engine did not load.
 */
export function mergeBeyondCap<Row extends { id?: string | null }>(engineRows: readonly Row[], pagedRows: readonly Row[]): { rows: Row[]; extra: Row[] } {
  const known = new Set(engineRows.map((r) => r.id).filter(Boolean));
  const extra = pagedRows.filter((r) => r.id && !known.has(r.id));
  return { rows: extra.length ? [...engineRows, ...extra] : [...engineRows], extra };
}

export interface BeyondCap<Row> {
  /** The engine's read came back full: there may be more contacts than it loaded. */
  capped: boolean;
  status: 'idle' | 'loading' | 'error' | 'ready';
  data: AllPages<Row> | undefined;
  error: unknown;
  retry: () => void;
}

export function useProspectListBeyondCap<Row>({ engineQueueLength, callerId, customerId }: {
  engineQueueLength: number;
  callerId: string | null | undefined;
  customerId: string | null | undefined;
}): BeyondCap<Row> {
  const capped = engineQueueLength >= ENGINE_QUEUE_LIMIT;
  const q = useQuery({
    queryKey: [PROSPECT_LIST_ALL_PAGES_KEY, callerId ?? null, customerId ?? null],
    queryFn: ({ signal }) => fetchAllProspectPages<Row>(customerId ? { customer_id: customerId } : {}, undefined, MAX_LIST_PAGES, signal),
    enabled: capped && !!callerId,
    refetchInterval: capped ? BEYOND_CAP_REFRESH_MS : false,
    staleTime: BEYOND_CAP_REFRESH_MS / 2,
  });
  const status: BeyondCap<Row>['status'] = !capped ? 'idle'
    : q.data ? 'ready'
    : q.isError ? 'error'
    : 'loading';
  return { capped, status, data: q.data, error: q.error, retry: () => { void q.refetch(); } };
}

// ── The phones of chosen lists, past the engine's cap (REV-W4-F6) ─────────

export interface ListsPhones {
  /** Sorted, unique: a stable filter whatever order the reads come back in. */
  phones: string[];
  /** Contacts read, against the lists' total (null when the server gave none). */
  loaded: number;
  total: number | null;
  complete: boolean;
}

/** Every phone of the given lists (one paged read per list, bounded like the Liste). */
export async function fetchListsPhones(
  sources: readonly string[],
  customerId: string | null | undefined,
  signal?: AbortSignal,
  post?: Post,
): Promise<ListsPhones> {
  const phones = new Set<string>();
  let loaded = 0;
  let total: number | null = 0;
  let complete = true;
  for (const source of sources) {
    const res = await fetchAllProspectPages<{ prospect_phone?: string | null }>(
      { source, ...(customerId ? { customer_id: customerId } : {}) }, post, MAX_LIST_PAGES, signal,
    );
    for (const p of res.items) if (p.prospect_phone) phones.add(p.prospect_phone);
    loaded += res.items.length;
    total = total === null || res.total === null ? null : total + res.total;
    complete = complete && res.complete;
  }
  return { phones: [...phones].sort(), loaded, total, complete };
}

/**
 * The history's « par liste » filter (and its CSV) read the phones from the
 * engine's capped queue: past ENGINE_QUEUE_LIMIT, the older contacts' calls
 * were silently left out (T-d3). When the engine's queue is full, the chosen
 * lists are read through the paginated route.
 */
export function useListsPhonesBeyondCap({ sources, customerId, engineQueueLength }: {
  sources: readonly string[];
  customerId: string | null | undefined;
  engineQueueLength: number;
}) {
  const capped = engineQueueLength >= ENGINE_QUEUE_LIMIT;
  const sorted = [...sources].sort();
  const q = useQuery({
    queryKey: [PROSPECT_LIST_ALL_PAGES_KEY, 'phones', customerId ?? null, sorted],
    queryFn: ({ signal }) => fetchListsPhones(sorted, customerId, signal),
    enabled: capped && sorted.length > 0,
    staleTime: BEYOND_CAP_REFRESH_MS,
  });
  const status: 'idle' | 'loading' | 'error' | 'ready' = !capped || sorted.length === 0 ? 'idle'
    : q.data ? 'ready'
    : q.isError ? 'error'
    : 'loading';
  return { capped, status, data: q.data, error: q.error, retry: () => { void q.refetch(); } };
}
